import { hideOnScrollClasses, useHideOnScroll } from '@/hooks';
import useBannerCollapseStore from '@/hooks/Scroll/useBannerCollapseStore';
import cx from 'classnames';
import { useLayoutEffect, useRef, useState } from 'react';
import { usePageBannerConfig } from './hooks/usePageBannerConfig';
import styles from './PageBanner.module.css';
import Navigation from './Slots/Navigation';
import { Slot, SlotProps } from './Slots/Slot';
import Title from './Slots/Title';
import { BannerSlotKeys } from './types';

const TitleAction = ({ children, className }: Partial<SlotProps>) => (
  <Slot id={BannerSlotKeys.TitleAction} className={className}>
    {children}
  </Slot>
);

const TitleMeta = ({ children, className }: Partial<SlotProps>) => (
  <Slot id={BannerSlotKeys.TitleMeta} className={className}>
    {children}
  </Slot>
);
const Note = ({ children, className }: Partial<SlotProps>) => (
  <Slot id={BannerSlotKeys.Note} className={className}>
    {children}
  </Slot>
);

const PageBanner = () => {
  const pageHeaderRef = useRef<HTMLDivElement>(null);
  const headerContentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isSticky, variant, hidden, dynamicHeight, isTall, syncCollapse } = usePageBannerConfig();
  const isDynamic = !!(isSticky && dynamicHeight);
  const isBannerCollapsed = useBannerCollapseStore(state => state.isCollapsed);

  useHideOnScroll({
    targetRef: pageHeaderRef,
    hideOnScrollClass: isDynamic ? hideOnScrollClasses.dynamic : hideOnScrollClasses.offset120,
    onToggleCompact: setIsScrolled,
    overrideHidden: syncCollapse ? isBannerCollapsed : undefined,
  });

  useLayoutEffect(() => {
    const contentElement = headerContentRef.current;
    if (!contentElement) return undefined;

    const updateMeasuredHeight = () => {
      document.documentElement.style.setProperty('--ces-banner-measured-height', `${contentElement.offsetHeight}px`);
    };

    updateMeasuredHeight();
    const resizeObserver = new ResizeObserver(updateMeasuredHeight);
    resizeObserver.observe(contentElement);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={pageHeaderRef}
      data-banner-variant={variant}
      data-banner-scrolled={isScrolled || undefined}
      data-banner-dynamic={dynamicHeight || undefined}
      data-banner-tall={isTall || undefined}
      className={cx(styles.header, {
        [styles.sticky]: isSticky,
        [styles.hidden]: hidden,
      })}
    >
      <div ref={headerContentRef} className={styles.headerContent}>
        <div id={BannerSlotKeys.Navigation} />

        <div className={cx(styles.titleContainer)}>
          <div id={BannerSlotKeys.Title} />
          <div id={BannerSlotKeys.TitleAction} className={styles.titleAction} />
          <div id={BannerSlotKeys.TitleMeta} />
        </div>

        <div id={BannerSlotKeys.Note} className={styles.noteContainer} />
      </div>
    </div>
  );
};

PageBanner.Title = Title;
PageBanner.TitleAction = TitleAction;
PageBanner.TitleMeta = TitleMeta;
PageBanner.Navigation = Navigation;
PageBanner.Note = Note;

export default PageBanner;
